"use client"

import { useState, useEffect } from "react"
import { Button } from "./ui/button"
import { Card } from "./ui/card"
import { Trash2, Eye, EyeOff, Move, RotateCw, Maximize2, SaveIcon, ShirtIcon, User2Icon, Move3dIcon, Layers } from "lucide-react"
import { cn } from "../lib/utils"
import { ReactComponent as ShortIcon } from '../icons/new/short.svg';
import { ReactComponent as SocksIcon } from '../icons/new/socks.svg';

type TransformMode = "translate" | "rotate" | "scale"

interface SceneObject {
  id: string
  name: string
  type: string
}

interface MultipleStateProps {
  onTransformModeChange?: (mode: TransformMode) => void
  onTransformVisibilityChange?: (visible: boolean) => void
  onObjectHighlight?: (objectId: string, visible: boolean) => void
  onObjectDelete?: (objectId: string) => void
  onReset?: () => void
}

declare global {
  interface Window {
    sceneAPI?: {
      addObject: (object: Omit<SceneObject, "id"> & { id?: string }) => void
      removeObject: (objectId: string) => void
      getObjects: () => SceneObject[]
      clearObjects: () => void
    }
  }
}

export default function MultipleState({
  onTransformModeChange,
  onTransformVisibilityChange,
  onObjectHighlight,
  onObjectDelete,
  onReset,
}: MultipleStateProps) {
  const [transformMode, setTransformMode] = useState<TransformMode>("translate")
  const [transformVisible, setTransformVisible] = useState(true)
  const [objects, setObjects] = useState<SceneObject[]>([])
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>({})

  useEffect(() => {
    window.sceneAPI = {
      addObject: (object) => {
        const newObject: SceneObject = {
          id: object.id || `obj-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name: object.name,
          type: object.type,
        }

        setObjects((prev) => {
          if (prev.some((o) => o.id === newObject.id)) return prev
          return [...prev, newObject]
        })

        setVisibilityMap(prev => ({ ...prev, [newObject.id]: true }))
      },

      removeObject: (objectId) => {
        setObjects(prev => prev.filter(o => o.id !== objectId))
        setVisibilityMap(prev => {
          const copy = { ...prev }
          delete copy[objectId]
          return copy
        })
      },

      getObjects: () => objects,

      clearObjects: () => {
        setObjects([])
        setVisibilityMap({})
      }
    }

    return () => {
      delete window.sceneAPI
    }
  }, [objects])

  const handleTransformMode = (mode: TransformMode) => {
    (window as any)["transform_mult_state"](mode)
    setTransformMode(mode)
    onTransformModeChange?.(mode)
  }

  const handleTransformVisibility = () => {
    const newState = !transformVisible
      ; (window as any)["transform_mult_visibility"](newState)
    setTransformVisible(newState)
    onTransformVisibilityChange?.(newState)
  }

  const handleHighlight = (objectId: string) => {
    setVisibilityMap(prev => {
      const newState = !prev[objectId]
        ; (window as any)["highlight_model"](objectId, newState)
      onObjectHighlight?.(objectId, newState)
      return { ...prev, [objectId]: newState }
    })
  }

  const handleDelete = (objectId: string) => {
    ; (window as any)["delete_model_mult"](objectId)
    setObjects(prev => prev.filter(o => o.id !== objectId))
    setVisibilityMap(prev => {
      const copy = { ...prev }
      delete copy[objectId]
      return copy
    })
    onObjectDelete?.(objectId)
  }

  const handleSave = () => {
    ; (window as any)["save_mult"]()
  }

  const handleSaveModularSetup = () => {
    ; (window as any)["save_modular_setup"]()
  }

  const handleMultPart = (part: any, objectId: string) => {

    if ((window as any).groupMore.children[objectId].getObjectByName(part)) {
      (window as any).groupMore.children[objectId].getObjectByName(part).visible = !(window as any).groupMore.children[objectId].getObjectByName(part).visible;
      (window as any).groupMore.children[objectId].userData[part] = (window as any).groupMore.children[objectId].getObjectByName(part).visible;
    }
  }

  const handleHideAll = () => {
    (window as any)["handleHideAll"]();
  }

  return (
    <div className="flex flex-col gap-6 p-0 w-full max-w-md bg-card/85 backdrop-blur-sm p-2 rounded-lg">
      <h2 className="text-lg font-semibold text-primary">Gerenciar Múltiplos</h2>

      <Card className="p-4">
        <h3 className="text-sm font-medium mb-3">Controles de Transformação</h3>

        <div className="flex gap-2 mb-3">
          <Button size="sm" className="flex-1" variant={transformMode === "translate" ? "default" : "outline"} onClick={() => handleTransformMode("translate")}>
            <Move className="w-4 h-4 mr-2" />Mover
          </Button>
          <Button size="sm" className="flex-1" variant={transformMode === "rotate" ? "default" : "outline"} onClick={() => handleTransformMode("rotate")}>
            <RotateCw className="w-4 h-4 mr-2" />Rodar
          </Button>
          <Button size="sm" className="flex-1" variant={transformMode === "scale" ? "default" : "outline"} onClick={() => handleTransformMode("scale")}>
            <Maximize2 className="w-4 h-4 mr-2" />Escala
          </Button>
        </div>

        {/**<Button size="sm" className="w-full" variant={transformVisible ? "default" : "secondary"} onClick={handleTransformVisibility}>
          {transformVisible ? <><Eye className="w-4 h-4 mr-2" />Controles Visíveis</> : <><EyeOff className="w-4 h-4 mr-2" />Controles Ocultos</>}
        </Button> */}
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-medium mb-3">Objetos na Cena</h3>

        {objects.map(obj => (
          <div key={obj.id} className="flex flex-col items-center justify-between p-3 rounded-md border m-1">
            <span className="text-sm font-medium">{obj.name}</span>

            <div className="flex gap-2">

              <Button size="icon" variant="ghost" onClick={() => handleMultPart("AVATAR", obj.id)}>
                <User2Icon></User2Icon>
              </Button>

              <Button size="icon" variant="ghost" onClick={() => handleMultPart("camisa", obj.id)}>
                <ShirtIcon></ShirtIcon>
              </Button>

              <Button size="icon" variant="ghost" onClick={() => handleMultPart("short", obj.id)}>
                <ShortIcon />
              </Button>

              <Button size="icon" variant="ghost" onClick={() => handleMultPart("MEIÃO", obj.id)}>
                <SocksIcon />
              </Button>

              <Button size="icon" variant="ghost" onClick={() => handleHighlight(obj.id)}>
                <Move3dIcon></Move3dIcon>
                {/**{visibilityMap[obj.id] ? <Eye className="w-4 h-4 text-primary" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />} */}
              </Button>

              <Button size="icon" variant="ghost" onClick={() => handleDelete(obj.id)} className="text-destructive">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </Card>

      <div className="flex flex-col">
        <Button onClick={handleHideAll} className="w-full text-primary mt-1" variant="outline">
          <Move3dIcon className="w-4 h-4 mr-2"></Move3dIcon> Esconder Todas as Flechas
        </Button>
        <Button onClick={handleSave} className="w-full text-primary mt-1" variant="outline">
          <SaveIcon className="w-4 h-4 mr-2" />Salvar como Cena Múltipla
        </Button>
        <Button onClick={handleSaveModularSetup} className="w-full text-primary mt-1" variant="outline">
          <Layers className="w-4 h-4 mr-2" />Salvar como Setup Modular
        </Button>
      </div>

    </div>
  )
}
